import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { FancySparkles } from "design-system/atoms/icons/FancySparkles"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { CompanyClientCell } from "modules/customers/components/CompanyClientCell"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { CustomerCell } from "modules/shared/components/CustomerCell"
import { useConversationQuestionsQuery } from "modules/support/hooks/useConversationQuestionsQuery"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { SearchSchema } from "routes/_base/_auth/support/list/route"
import { SupportConversationQuestionsResponseDataSchema, SupportSortFields } from "schema"
import { z } from "zod"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof SupportConversationQuestionsResponseDataSchema>[number]
>()

type ConversationQuestionsQueryReturn = ReturnType<typeof useConversationQuestionsQuery>

type Props = {
  questions: ConversationQuestionsQueryReturn["data"]
  isPending: ConversationQuestionsQueryReturn["isPending"]
  fields: typeof SupportSortFields
  onSort: (field: (typeof SupportSortFields)[number]) => void
  search: {
    field: (typeof SupportSortFields)[number]
    order: "asc" | "desc"
    supportTab?: z.infer<typeof SearchSchema>["supportTab"]
  }
}

export function ConversationQuestionsTable({
  questions,
  isPending,
  fields,
  onSort,
  search,
}: Props) {
  const { t } = useTranslation()
  const isAmphoraCompany = useIsAmphoraCompany()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          id: "resolvedAutomatically",
          header: () => null,
          cell: ({ row }) => {
            if (!row.original.resolvedAutomaticallyAt) return

            return (
              <Tooltip>
                <TooltipTrigger>
                  <FancySparkles size={16} strokeWidth={1} />
                </TooltipTrigger>
                <TooltipContent side="right">
                  <span className="px-1">{t("support.questions.table.resolvedAutomatically")}</span>
                </TooltipContent>
              </Tooltip>
            )
          },
          meta: {
            className: "w-px pr-4",
          },
        }),
        ...(!isAmphoraCompany
          ? [
              columnHelper.accessor("customer", {
                header: () => t("support.questions.table.customer"),
                cell: ({ row }) => {
                  return <CustomerCell customer={row.original.customer} />
                },
                meta: {
                  colSpan: 2,
                  className: "min-w-32 max-w-0 truncate",
                },
              }),
            ]
          : []),
        ...(isAmphoraCompany
          ? [
              columnHelper.display({
                id: "client",
                header: t("support.questions.table.client"),
                cell: ({ row }) => <CompanyClientCell client={row.original.customer.client} />,
                meta: {
                  className: "min-w-24 max-w-0 truncate",
                },
              }),
              columnHelper.display({
                id: "orderId",
                header: t("support.questions.table.orderId"),
                cell: ({ row }) => row.original.conversation.orderId ?? "-",
                meta: {
                  className: "min-w-24 max-w-0 truncate",
                },
              }),
            ]
          : []),
        columnHelper.accessor("campaign", {
          header: t("support.questions.table.campaign"),
          cell: (props) => props.getValue()?.name ?? "-",
          meta: {
            colSpan: 2,
            className: "min-w-24 max-w-0 truncate",
          },
        }),
        columnHelper.accessor("summary", {
          header: t("support.questions.table.summary"),
          cell: (props) => (
            <Tooltip>
              <TooltipTrigger className="block max-w-full truncate">
                {props.getValue()}
              </TooltipTrigger>
              <TooltipContent className="max-w-80">{props.getValue()}</TooltipContent>
            </Tooltip>
          ),
          meta: {
            colSpan: 4,
            className: "w-full min-w-48 max-w-0",
          },
        }),
        columnHelper.accessor("createdAt", {
          header: t("support.questions.table.createdAt"),
          cell: (props) => <DateDisplay date={props.getValue()} />,
          meta: {
            colSpan: 1,
            align: "right",
            className: "w-px",
          },
        }),
      ]),
    [isAmphoraCompany, t],
  )

  const table = useTable({
    data: questions ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || questions === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (questions.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("support.questions.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        navigate={{
          to: "/support/list/$customerId",
          params: { customerId: row.original.customer.id },
          search: {
            ...search,
            conversationId: row.original.conversationId
              ? Number(row.original.conversationId)
              : undefined,
            tab: "support",
            supportTab: search.supportTab || "questions",
            ...search,
          },
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<
      Features,
      z.infer<typeof SupportConversationQuestionsResponseDataSchema>[number],
      unknown
    >,
  ) {
    if (!fields.includes(header.id as (typeof SupportSortFields)[number])) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as (typeof SupportSortFields)[number]),
    }
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                sortable={buildSortable(header)}
                {...header.column.columnDef.meta}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
